import React, { useEffect, useRef } from "react";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieSettingsProps {
  preferences: CookiePreferences;
  onPreferencesChange: (preferences: CookiePreferences) => void;
  onSave: () => void;
  onClose: () => void;
}

/** Module-scope constant — avoids re-allocating selector string on every render */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** Returns all focusable elements within a container */
const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

const CookieSettings: React.FC<CookieSettingsProps> = ({
  preferences,
  onPreferencesChange,
  onSave,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus close button on mount
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  // Focus trap: Tab/Shift+Tab stays inside modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;

      const focusable = getFocusableElements(dialog);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleToggle = (type: keyof CookiePreferences) => {
    if (type === "essential") return; // Essential cookies cannot be disabled

    onPreferencesChange({
      ...preferences,
      [type]: !preferences[type],
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-settings-title"
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2
              id="cookie-settings-title"
              className="text-2xl font-bold text-neutral-900"
            >
              Ustawienia cookies
            </h2>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              aria-label="Zamknij ustawienia cookies"
              className="text-neutral-400 hover:text-neutral-600 text-2xl min-w-[44px] min-h-[44px] flex items-center justify-center rounded"
            >
              &times;
            </button>
          </div>

          <div className="space-y-6">
            {/* Essential Cookies */}
            <div className="border-b border-neutral-200 pb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Niezbędne cookies
                </h3>
                <div className="flex items-center">
                  <span className="mr-2 text-sm text-neutral-600">
                    Zawsze aktywne
                  </span>
                  <div
                    className="w-12 h-6 bg-green-500 rounded-full relative"
                    aria-hidden="true"
                  >
                    <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 right-0.5"></div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-neutral-600">
                Te cookies są niezbędne do działania strony. Umożliwiają
                podstawowe funkcjonalności jak nawigacja i dostęp do
                bezpiecznych obszarów. Nie można ich wyłączyć.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="border-b border-neutral-200 pb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Cookies analityczne
                </h3>
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => handleToggle("analytics")}
                      aria-label="Włącz cookies analityczne"
                      className="sr-only"
                    />
                    <div
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        preferences.analytics ? "bg-primary-600" : "bg-neutral-300"
                      }`}
                      aria-hidden="true"
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${
                          preferences.analytics
                            ? "transform translate-x-6"
                            : "translate-x-0.5"
                        }`}
                      ></div>
                    </div>
                  </div>
                </label>
              </div>
              <p className="text-sm text-neutral-600">
                Te cookies pozwalają nam analizować ruch na stronie i lepiej
                zrozumieć, jak użytkownicy korzystają z naszych ofert. Pomagają
                nam ulepszać nasze usługi.
              </p>
              <p className="text-xs text-neutral-500 mt-2">
                Procesory: Microsoft Clarity, Google Analytics
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className="pb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Cookies marketingowe
                </h3>
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={() => handleToggle("marketing")}
                      aria-label="Włącz cookies marketingowe"
                      className="sr-only"
                    />
                    <div
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        preferences.marketing ? "bg-primary-600" : "bg-neutral-300"
                      }`}
                      aria-hidden="true"
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${
                          preferences.marketing
                            ? "transform translate-x-6"
                            : "translate-x-0.5"
                        }`}
                      ></div>
                    </div>
                  </div>
                </label>
              </div>
              <p className="text-sm text-neutral-600">
                Te cookies są używane do dostarczania spersonalizowanych reklam
                i treści marketingowych. Mogą być ustawiane przez naszych
                partnerów reklamowych.
              </p>
              <p className="text-xs text-neutral-500 mt-2">
                Procesory: Meta Pixel, Google Tag Manager (reklamy)
              </p>
            </div>
          </div>

          <div className="flex space-x-4 pt-6 border-t border-neutral-200">
            <button
              onClick={onSave}
              className="flex-1 bg-primary-700 text-white px-6 py-3 rounded-lg hover:bg-primary-800 transition-colors font-medium"
            >
              Zapisz ustawienia
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors font-medium"
            >
              Anuluj
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieSettings;
